import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import {
  IFilter,
  ILogGenerico,
  ILogMensaje,
  IQueryParam,
  TipoEntidadLog,
} from 'modelos/src';
import { Subscription } from 'rxjs';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import {
  IColumnas,
  TablaComponent,
} from '../../../../auxiliares/tabla/tabla.component';
import {
  IFiltroTabla,
  IRegExpSearch,
} from '../../../../auxiliares/tabla/filtro/filtro.component';
import { DetallesLogGenericoComponent } from '../detalles-log-generico/detalles-log-generico.component';
import { fechaLogHtml } from '../fecha-log';

/**
 * Frames crudos de un dispositivo (tracker o alarma), tal como llegaron al
 * servidor de protocolo. Sirve para dar de alta códigos que todavía no están
 * configurados: se ve el frame y, cuando el parser sacó un código, si ese
 * código matcheó contra los `ICodigosDispositivo` del modelo.
 *
 * Va SOLO como solapa del detalle del dispositivo — no tiene ruta propia ni
 * entrada en el menú de Logs: sin `idEntidad` el listado no significa nada.
 */
@Component({
  selector: 'app-listado-logs-crudos',
  templateUrl: './listado-logs-crudos.component.html',
  styleUrl: './listado-logs-crudos.component.scss',
  standalone: true,
  imports: [TablaComponent, DetallesLogGenericoComponent],
})
export class ListadoLogsCrudosComponent implements OnInit, OnDestroy {
  private lista2 = inject(Lista2Service);
  helper = inject(HelperService);

  /** Dispositivo del que se muestran los frames. */
  @Input({ required: true }) idEntidad?: string;
  @Input({ required: true }) tipoEntidad?: TipoEntidadLog;

  public logs?: ILogGenerico[];
  public totalCount = 0;
  public columnas?: IColumnas<ILogGenerico>[];
  public logSeleccionado?: ILogGenerico;
  private logs$?: Subscription;

  /**
   * El nombre define dónde se guarda el tamaño de página elegido, así que se
   * separa por tipo: el tráfico de una alarma y el de un tracker se miran de
   * formas distintas.
   */
  public get name(): string {
    return `ListadoLogsCrudos${this.tipoEntidad ?? ''}Component`;
  }

  // FILTROS
  public search: IRegExpSearch = {
    fields: ['valores.mensaje', 'valores.hex', 'valores.codigo'],
  };
  private fechaCreacion: IFiltroTabla = {
    filter: { field: 'fechaCreacion' },
    label: 'Buscar por fecha',
    tipo: 'dateRange',
  };
  public filtros: IFiltroTabla[] = [this.fechaCreacion];

  public filter: Partial<IFilter<ILogGenerico>> = {};
  public query: IQueryParam = {
    filter: JSON.stringify(this.filter),
    sort: '-fechaCreacion',
    page: 0,
    // El tamaño se lee en ngOnInit, cuando `name` ya conoce el tipoEntidad:
    // es la clave con la que la tabla lo guarda.
    limit: 10,
    includeChildren: true,
    childrenLevel: 0,
  };

  public async listar(): Promise<void> {
    if (!this.idEntidad) return;

    // El tipo va en el filtro además del id: el backend lo fuerza igual según
    // el permiso, pero así la consulta dice lo que quiere y no depende de eso.
    const filter = HelperService.stringToObject(this.query.filter);
    filter['idEntidad'] = this.idEntidad;
    filter['tipoEntidad'] = this.tipoEntidad;
    this.query.filter = JSON.stringify(filter);

    this.logs$?.unsubscribe();
    // Refresco corto: se mira mientras el equipo está reportando.
    this.logs$ = this.lista2.logsGenericos(this.query, 5).subscribe((data) => {
      this.totalCount = data.totalCount;
      this.logs = data.datos;
    });
  }

  public verDetalles(dato: ILogGenerico): void {
    this.logSeleccionado = dato;
  }

  public cerrarDetalles(): void {
    this.logSeleccionado = undefined;
  }

  public async copiar(valor?: string): Promise<void> {
    if (!valor) return;
    try {
      await navigator.clipboard.writeText(valor);
      this.helper.notifSuccess('Copiado al portapapeles');
    } catch (error) {
      console.error('Error al copiar al portapapeles:', error);
    }
  }

  /**
   * Cuántas veces llegó la trama: la fila más las copias que el servidor de
   * alarmas contó y no guardó. `repetido` lo escribe irix-alarmas-go y
   * todavía no está en `ILogMensaje`, por eso el tipo se amplía acá.
   */
  private veces(dato: ILogGenerico): number {
    const valores = dato?.valores as
      | (ILogMensaje & { repetido?: number })
      | undefined;
    return (valores?.repetido ?? 0) + 1;
  }

  /////////////////////////////////////////////////////////////
  // Tabla

  /**
   * El `html` de una columna se pinta con `bypassSecurityTrustHtml`: Angular
   * NO lo sanitiza. Acá el contenido lo escribe el dispositivo —son los bytes
   * que llegaron por el socket—, así que todo lo que salga de `valores` va
   * escapado antes de concatenarse al HTML.
   */
  private escaparHtml(valor: string): string {
    return valor
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private setColumnas() {
    const columnas: IColumnas<ILogGenerico>[] = [
      // Fecha
      {
        header: { label: 'Fecha', sortable: true, sort: 'fechaCreacion' },
        row: {
          // Con segundos: en los logs importan
          html: (dato) => fechaLogHtml(dato.fechaCreacion),
          noData: 'Sin fecha',
        },
      },
      // Protocolo
      {
        header: { label: 'Protocolo' },
        row: {
          html: (dato): string => {
            const transporte = dato?.valores?.protocolo;
            const parser = dato?.valores?.nombreProtocolo;
            if (parser && transporte) {
              return this.escaparHtml(`${parser} (${transporte})`);
            }
            return this.escaparHtml(parser || transporte || '');
          },
          noData: 'Sin protocolo',
        },
      },
    ];

    // Veces: solo para alarmas. irix-alarmas-go no guarda las copias exactas
    // de una trama que el mismo comunicador repite dentro de los 30 s (el
    // panel reintenta porque el ACK le llegó tarde): deja la fila original
    // con `valores.repetido` = cuántas copias descartó. Sin esta columna una
    // trama que llegó 40 veces se ve igual que una que llegó una. Los
    // trackers guardan una fila por frame, así que ahí no hay nada que decir.
    if (this.tipoEntidad === 'Alarma') {
      columnas.push({
        header: { label: 'Veces', ancho: 0.6 },
        row: {
          html: (dato): string => `×${this.veces(dato)}`,
        },
      });
    }

    // Código y Parseo solo los escribe el pipeline de trackers
    // (`RawLogger.documento` en gestion-trackers-go): saca el código del frame
    // y lo matchea contra los `ICodigoDispositivo` del modelo. El camino de
    // alarmas (`registrarLogCrudo` en gestion-api-alarmas) guarda el frame y
    // nada más, así que ahí las dos columnas quedan siempre vacías y solo ocupan el
    // ancho que necesita el frame.
    if (this.tipoEntidad !== 'Alarma') {
      columnas.push(
        // Código extraído
        {
          header: { label: 'Código' },
          row: {
            html: (dato): string => {
              const codigo = dato?.valores?.codigo;
              return codigo
                ? `<span class="tabla-codigo">${this.escaparHtml(codigo)}</span>`
                : '';
            },
            noData: 'Sin código',
          },
        },
        // Resultado del parseo: "No configurado" es lo que se viene a buscar.
        {
          header: { label: 'Parseo' },
          row: {
            estado: (dato) => {
              const parseo = dato?.valores?.parseo;
              if (!parseo) return undefined;
              if (parseo === 'OK') {
                return { texto: parseo, tono: 'ok', icono: 'check_circle' };
              }
              if (parseo === 'No configurado') {
                return { texto: parseo, tono: 'aviso', icono: 'warning' };
              }
              return { texto: parseo, tono: 'error', icono: 'error' };
            },
            noData: 'Sin parseo',
          },
        },
      );
    }

    columnas.push(
      // Frame
      {
        header: { label: 'Frame' },
        row: {
          html: (dato): string => {
            // Los protocolos binarios (gt06, huabao) no se leen como texto:
            // ahí lo útil es el hex. Los de texto, al revés.
            const mensaje = dato?.valores?.mensaje?.trim();
            const legible = mensaje && /^[\x20-\x7E\r\n\t]+$/.test(mensaje);
            const valor = legible ? mensaje : dato?.valores?.hex;
            if (!valor) return '';
            // El corte lo hace el CSS, no un `substring`: el ancho de la celda
            // no se sabe desde acá, así que cortar por cantidad de caracteres
            // deja el '…' fuera de la vista y parece texto tajeado. Con
            // `text-overflow` el '…' cae siempre en el borde de la celda. El
            // tope de 500 es solo para no meter un frame absurdo en el DOM; el
            // completo está en el `title` y en el detalle.
            const acotado =
              valor.length > 500 ? valor.substring(0, 500) + '…' : valor;
            return (
              `<span class="tabla-codigo" style="display: block;` +
              ` white-space: nowrap; overflow: hidden; text-overflow: ellipsis"` +
              ` title="${this.escaparHtml(valor)}">` +
              `${this.escaparHtml(acotado)}</span>`
            );
          },
          noData: 'Sin frame',
        },
      },
    );

    columnas.push({
      header: { label: 'Acciones' },
      row: {
        // Acciones sueltas, como estaban (sin menú)
        acciones: [
          {
            tipo: 'material',
            icon: 'content_copy',
            tooltip: 'Copiar HEX',
            click: (dato) => this.copiar(dato?.valores?.hex),
          },
          {
            tipo: 'material',
            icon: 'visibility',
            tooltip: 'Ver detalle',
            click: (dato) => this.verDetalles(dato),
          },
        ],
      },
    });

    this.columnas = columnas;
  }

  // Hooks

  async ngOnInit() {
    this.query.limit = +this.helper.getPageSize(this.name);
    this.setColumnas();
    await this.listar();
  }

  async ngOnDestroy() {
    this.logs$?.unsubscribe();
  }
}
